import { useEffect, useMemo, useRef, useState } from 'react';
import { GastoPlan, PagoPlan, Plan } from '../types';
import { useAuth } from '../context/AuthContext';
import { useData } from '../context/DataContext';
import { usePerfil } from '../context/PerfilContext';
import { useSincronizacionAMedias } from '../context/SincronizacionAMediasContext';
import {
  diferencias, hayDiferencias, movimientosEsperados, separarPorAutor, DiferenciasSincronizacion,
} from '../utils/sincronizacionAMedias';

const VACIO: DiferenciasSincronizacion = { crear: [], actualizar: [], borrar: [] };

/**
 * Mantiene las Finanzas del usuario al día con un plan de A medias.
 *
 * Lo que apuntaste tú se pasa solo (desde el vigía global, `autoAplicar`). Lo que apuntaron otros espera a que lo
 * confirmes (`pendientes` + `pasarPendientes`), salvo que el plan tenga la
 * sincronización automática.
 *
 * Solo calcula cuando ya han llegado las transacciones, los movimientos del
 * plan y la configuración: con cualquiera de ellos a medio cargar, una lista
 * vacía parecería «falta todo» y se duplicarían movimientos.
 */
export function useSincronizacionPlan(
  plan: Plan | undefined, gastos: GastoPlan[], pagos: PagoPlan[], movimientosCargados: boolean,
  /** Solo una instancia por plan debe aplicar sola los cambios (el vigía de AvisosAMedias). */
  autoAplicar = false,
) {
  const { user } = useAuth();
  const { tieneModulo } = usePerfil();
  const { transacciones, transaccionesCargadas, cuentas, aplicarSincronizacionPlan } = useData();
  const { sincronizacionDe, sincronizacionesCargadas } = useSincronizacionAMedias();

  const config = plan ? sincronizacionDe(plan.id) : undefined;
  const yo = plan?.participantes.find(p => !!user && p.uid === user.uid);
  const disponible = tieneModulo('finanzas') && !!yo;
  const listo = disponible && movimientosCargados && transaccionesCargadas && sincronizacionesCargadas;

  const generadas = useMemo(
    () => (plan ? transacciones.filter(t => t.planId === plan.id) : []), [transacciones, plan]);

  const todas = useMemo(() => {
    if (!listo || !plan || !yo) return VACIO;
    return diferencias(movimientosEsperados(plan, yo.id, gastos, pagos), generadas);
  }, [listo, plan, yo, gastos, pagos, generadas]);

  const { propias, ajenas } = useMemo(() => separarPorAutor(todas, user?.uid ?? ''), [todas, user]);
  const activa = !!config?.activa;
  const automaticas = !activa ? VACIO : config!.automatica ? todas : propias;
  const pendientes = activa && !config!.automatica ? ajenas : VACIO;

  const cuentaValida = !!config && cuentas.some(c => c.id === config.cuentaId);
  const [error, setError] = useState('');
  const aplicando = useRef(false);
  // Al acabar una escritura se fuerza otra vuelta, por si mientras tanto
  // llegó algo nuevo que el efecto se saltó por estar ocupado.
  const [vuelta, setVuelta] = useState(0);

  const aplicar = async (cambios: DiferenciasSincronizacion) => {
    if (!plan || !config) return;
    if (!cuentaValida) {
      // Sin cuentas aún es que no han llegado; con cuentas y sin la elegida, es que se borró
      if (cuentas.length > 0) setError('La cuenta elegida para este plan ya no existe. Elige otra en la sincronización.');
      return;
    }
    aplicando.current = true;
    try {
      await aplicarSincronizacionPlan(plan.id, cambios, config);
      setError('');
    } catch (e) {
      console.error(e);
      setError('No se pudieron pasar los movimientos a Finanzas.');
    } finally {
      aplicando.current = false;
      setVuelta(v => v + 1);
    }
  };

  useEffect(() => {
    // Tras un error no se reintenta en bucle: se espera a que el usuario actúe
    if (!autoAplicar || !activa || !listo || aplicando.current || error || !hayDiferencias(automaticas)) return;
    aplicar(automaticas);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoAplicar, activa, listo, automaticas, error, vuelta, cuentaValida]);

  return {
    /** Tiene Finanzas y participa en el plan: puede sincronizar. */
    disponible,
    listo,
    config,
    /** Transacciones de Finanzas que ya salieron de este plan. */
    generadas,
    /** Lo que habría que hacer si se sincronizara todo ahora (para avisar al activar). */
    todas,
    /** Lo que apuntaron otros, esperando confirmación. */
    pendientes,
    pasarPendientes: () => aplicar(pendientes),
    /** Saca de Finanzas todo lo que generó este plan. */
    quitarDeFinanzas: () => (plan && config
      ? aplicarSincronizacionPlan(plan.id, { crear: [], actualizar: [], borrar: generadas }, config)
      : Promise.resolve()),
    error,
    reintentar: () => setError(''),
  };
}
